import React, { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { state } from '../store/global';
import API from '../lib/api';
import NormalButton from '../components/common/NormalButton';
import LoadingBar from '../components/common/LoadingBar';
import InicioHub from '../components/InicioHub';

export default function Dashboard() {
  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isWalletCreationModal, setIsWalletCreationModal] = useState(false);
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [balance, setBalance] = useState(0.0);
  const [balanceCop, setBalanceCop] = useState(0.0);
  const [balanceUsdc, setBalanceUsdc] = useState(0.0);
  const [dualBalanceEnabled, setDualBalanceEnabled] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);

  useEffect(() => {
    const getWalletBalance = async () => {
      try {
        const res = await API.get('user/getDashboardInfo');

        setBalance(res.data.data.balance);
        setBalanceUsdc(res.data.data.balance_usdc ?? res.data.data.balance ?? 0);
        setBalanceCop(res.data.data.balance_cop ?? 0);
        setDualBalanceEnabled(!!res.data.data.dual_balance_enabled);
        const hasWallet = res.data.data.hasWallet !== undefined
          ? res.data.data.hasWallet
          : (res.data.data.balance !== null && res.data.data.balance !== undefined);
        setIsWalletCreationModal(hasWallet);
        setLoading(false);
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message;
        console.log('err: ', msg);
        if (msg == 'User not found') navigate('/login');
        else {
          try {
            const vaultRes = await API.get('/user/getVaultByUserId');
            if (vaultRes.data.success == 'success' && vaultRes.data.data) {
              setBalance(0.0);
              setIsWalletCreationModal(true);
              setLoading(false);
              return;
            }
          } catch (vaultErr) {
            // No vault found - show creation modal
          }
          setIsWalletCreationModal(false);
          setLoading(false);
        }
      }
    };

    getWalletBalance();

  }, []);

  const onCreateWallet = async () => {
    if (isCreatingWallet) return;

    setIsCreatingWallet(true);
    try {
      const res = await API.post('user/createWallet');
      console.log('Wallet created:', res.data);
      setBalance(0.0);
      setIsWalletCreationModal(true);
      setIsCreatingWallet(false);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message;
      console.error('Error creating wallet:', msg);
      setIsCreatingWallet(false);
      if (
        msg?.toLowerCase().includes('already') ||
        msg?.toLowerCase().includes('existe') ||
        msg?.toLowerCase().includes('duplicate') ||
        msg?.toLowerCase().includes('vault')
      ) {
        setIsWalletCreationModal(true);
      }
    }
  };

  const gotToPage = (path) => {
    navigate(path);
  };

  return (
    <>
      {!loading ? (
        <>
          {isWalletCreationModal ? (
            <div className="px-6 sm:px-10 py-6 sm:py-8">
              <InicioHub
                firstName={snap.user?.firstName || ''}
                balanceCop={balanceCop}
                balanceUsdc={balanceUsdc}
                balance={balance}
                dualBalanceEnabled={dualBalanceEnabled}
                onConvert={() => setShowConvertModal(true)}
                onNavigate={gotToPage}
              />
            </div>
          ) : (
            <div className="relative">
              <div className="fixed z-50 inset-0 bg-black opacity-30 w-full h-full"></div>
              <div className="z-51 fixed inset-0 flex items-center justify-center">
                <div className="font-[poppins] w-91 py-6 bg-white rounded-[20px]">
                  <div className="pb-2 px-6 border-b-1 border-b-[#E9EAEB] flex items-center justify-between">
                    <p className="font-base text-[#252B37] font-medium leading-[20px]">
                      Crear billetera
                    </p>
                    <button
                      onClick={() => setIsWalletCreationModal(true)}
                      className="cursor-pointer"
                      disabled={isCreatingWallet}
                    >
                      <X className="w-6 h-6 text-[#181D27]" />
                    </button>
                  </div>

                  <div className="px-4 py-4 space-y-4">
                    {isCreatingWallet ? (
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="w-12 h-12 border-4 border-[#092F7B] border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm text-[#535862] font-normal leading-[20px] text-center">
                          Creando tu billetera...
                        </p>
                        <p className="text-sm text-[#717680] font-normal leading-[20px] text-center">
                          Este proceso tardará unos segundos.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-[#535862] font-normal leading-[20px] text-center">
                          Antes de comenzar, necesitarás una billetera custodiada para tus dólares.
                        </p>
                        <p className="text-sm text-[#535862] font-normal leading-[20px] text-center">
                          Éste proceso tardará unos segundos.
                        </p>
                      </>
                    )}
                  </div>

                  <div className="px-6">
                    <NormalButton
                      customClass={`w-full py-3 ${
                        isCreatingWallet ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#092F7B]'
                      } text-[#F5F5F5]`}
                      text={isCreatingWallet ? 'Procesando...' : 'Crear billetera'}
                      onClick={onCreateWallet}
                      disabled={isCreatingWallet}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <LoadingBar />
      )}

      {showConvertModal && (
        <ConvertCurrencyModal
          balanceCop={balanceCop}
          balanceUsdc={balanceUsdc}
          onClose={() => setShowConvertModal(false)}
          onSuccess={(newBalances) => {
            setBalanceCop(newBalances.balance_cop);
            setBalanceUsdc(newBalances.balance_usdc);
            setShowConvertModal(false);
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// ConvertCurrencyModal — Modelo A (dual balance COP <-> USD)
// ============================================================================
function ConvertCurrencyModal({ balanceCop, balanceUsdc, onClose, onSuccess }) {
  const [direction, setDirection] = useState('COP_TO_USD');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rate, setRate] = useState(null);
  const [refreshIn, setRefreshIn] = useState(60);

  const fetchRate = () => {
    API.get('/transaction/get_exchange_rate')
      .then((res) => { if (res.data?.rate) setRate(res.data.rate); })
      .catch(() => {});
  };
  useEffect(() => { fetchRate(); }, []);
  useEffect(() => {
    const t = setInterval(() => {
      setRefreshIn((s) => {
        if (s <= 1) { fetchRate(); return 60; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const numericAmount = parseFloat(String(amount).replace(/[^0-9.]/g, '')) || 0;
  const fromMax = direction === 'COP_TO_USD' ? balanceCop : balanceUsdc;
  const exceedsBalance = numericAmount > fromMax;
  const feePct = parseFloat(import.meta.env.VITE_DEVELOPER_FEE) || 0.5;
  const feeAmount = numericAmount * (feePct / 100);
  const netAmount = numericAmount - feeAmount;
  const previewConverted = rate ? (direction === 'COP_TO_USD' ? netAmount / rate : netAmount * rate) : 0;

  const handleConvert = async () => {
    if (!numericAmount || exceedsBalance) return;
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/transaction/convert', { direction, amount: numericAmount });
      if (res.data?.success) {
        onSuccess({
          balance_cop: res.data.data.balance_cop,
          balance_usdc: res.data.data.balance_usdc,
        });
      } else {
        setError(res.data?.message || 'No se pudo realizar la conversion');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Error en el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-[20px] w-full max-w-md p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-[#0A2540]">Convertir</h3>
          <button onClick={onClose} className="text-2xl text-[#9CA3AF]">&times;</button>
        </div>

        <div className="flex bg-[#F3F4F6] rounded-[12px] p-1">
          <button
            onClick={() => setDirection('COP_TO_USD')}
            className={`flex-1 py-2 rounded-[10px] text-sm font-semibold ${
              direction === 'COP_TO_USD' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-[#6B7280]'
            }`}
          >
            Pesos a Dolares
          </button>
          <button
            onClick={() => setDirection('USD_TO_COP')}
            className={`flex-1 py-2 rounded-[10px] text-sm font-semibold ${
              direction === 'USD_TO_COP' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-[#6B7280]'
            }`}
          >
            Dolares a Pesos
          </button>
        </div>

        <div>
          <p className="text-xs text-[#6B7280] font-semibold mb-1">
            Cuanto quieres convertir ({direction === 'COP_TO_USD' ? 'COP' : 'USD'})
          </p>
          <input
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={direction === 'COP_TO_USD' ? '100.000' : '50.00'}
            className="w-full p-3 border-2 border-[#C9A961] rounded-[12px] text-2xl font-bold text-[#0A2540] text-center"
          />
          <p className="text-[11px] text-[#9CA3AF] text-center mt-1">
            Disponible: {direction === 'COP_TO_USD'
              ? `$ ${(fromMax || 0).toLocaleString('es-CO')} COP`
              : `$ ${(fromMax || 0).toFixed(2)} USD`}
          </p>
        </div>

        {rate && (
          <div className="bg-[#F0F4FF] rounded-[12px] px-3 py-2 flex justify-between items-center">
            <span className="text-xs font-semibold text-[#0A2540]">Cotizacion actual</span>
            <span className="text-xs font-bold text-[#0A2540]">1 USD = $ {rate.toLocaleString('es-CO')} COP</span>
          </div>
        )}
        {rate && (
          <p className="text-[11px] text-[#6B7280] text-center -mt-2">
            La cotizacion se actualizara en {refreshIn} segundos
          </p>
        )}

        {numericAmount > 0 && rate && (() => {
          const esCopOrigen = direction === 'COP_TO_USD';
          const fmtOrigen = (v) => esCopOrigen
            ? `$ ${Math.round(v).toLocaleString('es-CO')} COP`
            : `$ ${v.toFixed(2)} USD`;
          const fmtDestino = (v) => esCopOrigen
            ? `$ ${v.toFixed(2)} USD`
            : `$ ${Math.round(v).toLocaleString('es-CO')} COP`;
          return (
            <div className="bg-white border border-[#E5E7EB] rounded-[12px] p-3 space-y-1">
              <div className="flex justify-between text-xs text-[#6B7280]">
                <span>Monto a convertir</span>
                <span>{fmtOrigen(netAmount)}</span>
              </div>
              <div className="flex justify-between text-xs text-[#6B7280]">
                <span>Comision FlowexPay ({feePct}%)</span>
                <span>{fmtOrigen(feeAmount)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-[#0A2540] pt-1 border-t border-[#F3F4F6]">
                <span>Total a debitar</span>
                <span>{fmtOrigen(numericAmount)}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-[#059669]">
                <span>Recibes (aprox.)</span>
                <span>{fmtDestino(previewConverted)}</span>
              </div>
            </div>
          );
        })()}

        {exceedsBalance && (
          <p className="text-xs text-[#DC2626]">El monto excede tu saldo disponible.</p>
        )}
        {error && <p className="text-xs text-[#DC2626]">{error}</p>}

        <button
          onClick={handleConvert}
          disabled={!numericAmount || exceedsBalance || loading || !rate}
          className="w-full py-3 rounded-[12px] text-white font-bold disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #0A2540, #1A3A6C)' }}
        >
          {loading ? 'Procesando...' : 'Convertir ahora'}
        </button>
      </div>
    </div>
  );
}
